import { InfoCircleOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Drawer,
  Flex,
  Form,
  Grid,
  message,
  Space,
  Spin,
  Splitter,
} from 'antd';
import { Typography } from 'antd/lib';
import { Page } from 'osep-react-antd';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import packageJson from '../../package.json';
import Markdown from '../components/Markdown';
import Variable from '../components/variables/Variable';
import useCreateInfraProjectInstanceMutation from '../hooks/mutations/useCreateInfraInstanceMutation';
import useInfraProjectConfig from '../hooks/query/useInfraProjectConfig';
import useInfraProjectTemplateReadme from '../hooks/query/useInfraProjectTemplateReadme';
import useInfraProjectUsedUniqueValues from '../hooks/query/useInfraProjectUsedUniqueValues';
import {
  ConfigModel,
  TemplateModel,
  TemplateNamesByValueByVariable,
} from '../iasConfigSchema';
import findImageByProjectNameOrType from '../utils/findImageByProjectNameOrType';
import ErrorPage from './ErrorPage';

const InfraProjectTemplateReadme: React.FC<{
  projectId: string;
  template: TemplateModel;
}> = ({ projectId, template }) => {
  const { data, isLoading, error } = useInfraProjectTemplateReadme(
    projectId,
    template.name,
  );

  if (error) {
    console.error('Error loading README:', error);
  }

  return (
    <>
      {isLoading && (
        <Flex justify="center" align="center" style={{ height: '100%' }}>
          <Spin />
        </Flex>
      )}
      {!isLoading && data?.content && <Markdown>{data.content}</Markdown>}
      {!isLoading && !data?.content && (
        <Markdown>{template.description}</Markdown>
      )}
    </>
  );
};

const FormComponent: React.FC<{
  config: ConfigModel;
  usedUniqueValues: TemplateNamesByValueByVariable;
  projectId: string;
  template: TemplateModel;
  templateInfoActions: React.ReactNode[];
}> = ({
  config,
  usedUniqueValues,
  projectId,
  template,
  templateInfoActions,
}) => {
  const [loading, setLoading] = React.useState(false);
  const [form] = Form.useForm();

  const navigate = useNavigate();

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  const { createInfraInstance } = useCreateInfraProjectInstanceMutation();

  const variables = config.variables.filter(
    (variable) =>
      template?.variables.some(
        (variableName) => variableName === variable.name,
      ),
  );

  const handleSubmit = async (values: { [key: string]: any }) => {
    try {
      setLoading(true);

      await createInfraInstance(projectId, template.name, {
        values: Object.entries(values.variables).map(([key, value]) => ({
          variable_name: key,
          value: value,
        })),
      });

      message.success(
        'We will provision your infrastructure and notify you via email once it is ready.',
      );
    } catch (error) {
      message.error(
        'There was an error while provisioning the infrastructure. Please try again or contact the OSEP team.',
      );
      console.error('Error creating new instance:', error);
      return;
    } finally {
      setLoading(false);
    }

    navigate('/ias');
  };

  const defaultValues = {
    ...variables?.reduce((defaultValues, variable) => {
      if (variable.default !== undefined) {
        return { ...defaultValues, [variable.name]: variable.default };
      }

      return defaultValues;
    }, {}),
  };

  return (
    <Form
      form={form}
      layout="vertical"
      size="large"
      onFinish={handleSubmit}
      validateTrigger={['onSubmit', 'onChange']}
      initialValues={{ variables: defaultValues }}
    >
      <Space direction="vertical" size="large" style={{ display: 'flex' }}>
        <Card title={template.display_name} actions={templateInfoActions}>
          <Card.Meta
            title={template.short_description}
            description={<Markdown>{template.description}</Markdown>}
            avatar={
              <img
                src={findImageByProjectNameOrType(template.name)}
                height={80}
                alt={template.name}
              />
            }
          />
        </Card>

        <Card key={template.name} title={template.display_name}>
          <Space
            direction="vertical"
            size={'small'}
            style={{ display: 'flex' }}
          >
            {variables.map((variable) => (
              <Variable
                variable={variable}
                key={variable.name}
                templateName={template.name}
                usedUniqueValues={usedUniqueValues}
              />
            ))}
          </Space>
        </Card>
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            style={{ width: !screens.md ? '100%' : 'auto' }}
          >
            Provision Infrastructure
          </Button>
        </Form.Item>
      </Space>
    </Form>
  );
};

const ConfigureProjectPage: React.FC = () => {
  const { projectId, templateName } = useParams<{
    projectId: string;
    templateName: string;
  }>();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const navigate = useNavigate();
  const [isTemplateInfoModalVisible, setIsTemplateInfoModalVisible] =
    React.useState(false);

  const {
    data: projectConfig,
    isLoading: isProjectConfigLoading,
    error: projectConfigError,
  } = useInfraProjectConfig(projectId ? projectId : '', !!projectId);

  const {
    data: usedUniqueValuesData,
    isLoading: isUsedUniqueValuesLoading,
    error: usedUniqueValuesError,
  } = useInfraProjectUsedUniqueValues(projectId ? projectId : '', !!projectId);

  if (projectConfigError) {
    return <ErrorPage error={projectConfigError} />;
  }

  if (usedUniqueValuesError) {
    return <ErrorPage error={usedUniqueValuesError} />;
  }

  const config = projectConfig?.content as ConfigModel;
  const usedUniqueValues: TemplateNamesByValueByVariable =
    usedUniqueValuesData?.usedUniqueValues ?? {};

  const templateWithConfig = config?.templates
    .filter((template) => template.name === templateName)
    .map((template) => ({
      template,
      config: config,
      projectId: projectConfig?.projectId || '',
    }))
    .at(0);

  const isLoading = isProjectConfigLoading || isUsedUniqueValuesLoading;

  if (!isLoading && !templateWithConfig) {
    return (
      <ErrorPage
        error={`No template found for infrastructure project ${projectId}`}
      />
    );
  }

  const showSplitter = screens.xxl;

  return (
    <Page
      title="Configure Instance"
      onBack={() => navigate('/ias/add')}
      isLoading={isLoading}
      footer={<Typography.Text disabled>{packageJson.version}</Typography.Text>}
    >
      <Space direction="vertical" size="large" style={{ display: 'flex' }}>
        {templateWithConfig && showSplitter && (
          <Splitter>
            <Splitter.Panel collapsible>
              <div style={{ marginRight: 'min(2vw, 24px)' }}>
                <FormComponent
                  config={templateWithConfig.config}
                  usedUniqueValues={usedUniqueValues}
                  projectId={templateWithConfig.projectId}
                  template={templateWithConfig.template}
                  templateInfoActions={[]}
                />
              </div>
            </Splitter.Panel>
            <Splitter.Panel collapsible>
              <div style={{ marginLeft: 'min(2vw, 24px)' }}>
                <Card title="Template Information">
                  <InfraProjectTemplateReadme
                    projectId={templateWithConfig.projectId}
                    template={templateWithConfig.template}
                  />
                </Card>
              </div>
            </Splitter.Panel>
          </Splitter>
        )}
        {templateWithConfig && !showSplitter && (
          <>
            <FormComponent
              templateInfoActions={[
                <Button
                  icon={<InfoCircleOutlined />}
                  onClick={() => setIsTemplateInfoModalVisible(true)}
                >
                  More Information
                </Button>,
              ]}
              config={templateWithConfig.config}
              usedUniqueValues={usedUniqueValues}
              projectId={templateWithConfig.projectId}
              template={templateWithConfig.template}
            />
            <Drawer
              title="Template Information"
              open={isTemplateInfoModalVisible}
              onClose={() => setIsTemplateInfoModalVisible(false)}
              loading={isLoading}
              size="large"
            >
              <InfraProjectTemplateReadme
                projectId={templateWithConfig.projectId}
                template={templateWithConfig.template}
              />
            </Drawer>
          </>
        )}
      </Space>
    </Page>
  );
};

export default ConfigureProjectPage;
